import { useEffect, useState } from 'react';
import { layoutApi, type LayoutDef, type WidgetDef } from './api';

/** De actieve indeling (hubs en widgets), gedeeld door Home, menu en editor. */

let current: LayoutDef | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

export function loadLayout(force = false): Promise<void> {
  if (loading && !force) return loading;
  loading = layoutApi.current()
    .then((r) => {
      current = r.layout;
      listeners.forEach((l) => l());
    })
    .catch(() => undefined);
  return loading;
}

export function setLayout(l: LayoutDef): void {
  current = l;
  listeners.forEach((fn) => fn());
}

export function useLayout(): LayoutDef | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l = () => setTick((n) => n + 1);
    listeners.add(l);
    if (!current) void loadLayout();
    return () => {
      listeners.delete(l);
    };
  }, []);
  return current;
}

/** Waar staat deze hub bovenaan? undefined = nog niet ingesteld (dan geldt de automatische koppeling). */
export function pagesOf(h: LayoutDef['hubs'][number]): string[] | undefined {
  if (h.pages) return h.pages;
  if (h.library === 'none') return [];
  return h.library ? [h.library] : undefined;
}

/**
 * Welke hub hoort bij deze bibliotheek? Expliciet gekoppeld in de editor, of anders
 * vanzelf: hub "films" bij de eerste filmbibliotheek, "series" bij de eerste seriebibliotheek.
 */
export function hubForLibrary(
  layout: LayoutDef | null,
  sectionKey: string,
  libraries: { key: string; type: string }[],
): LayoutDef['hubs'][number] | undefined {
  if (!layout) return undefined;
  const explicit = layout.hubs.find((h) => pagesOf(h)?.includes(sectionKey));
  if (explicit) return explicit;
  const firstMovie = libraries.find((l) => l.type === 'movie')?.key;
  const firstShow = libraries.find((l) => l.type === 'show')?.key;
  return layout.hubs.find((h) => !pagesOf(h) && ((h.id === 'films' && sectionKey === firstMovie) || (h.id === 'series' && sectionKey === firstShow)));
}

/** Hubs die als knop op Home staan (de eerste is altijd Home zelf). */
export function homeHubs(layout: LayoutDef | null): LayoutDef['hubs'] {
  return (layout?.hubs ?? []).filter((h, i) => i === 0 || h.showOnHome !== false);
}

/** Menu-pagina's waar je een hub bovenaan kunt zetten (naast bibliotheken). */
export const HUB_PAGES: { id: string; label: string }[] = [
  { id: 'collections', label: 'Collecties' },
  { id: 'live', label: 'Live TV' },
  { id: 'mylist', label: 'Mijn lijst' },
  { id: 'calendar', label: 'Kalender' },
  { id: 'requests', label: 'Verzoeken' },
];

export function hubForPage(layout: LayoutDef | null, page: string): LayoutDef['hubs'][number] | undefined {
  return layout?.hubs.find((h) => pagesOf(h)?.includes(page));
}

/**
 * Een widget toevoegen aan Home (de eerste hub), direct onder de spotlight.
 * Bewaart in dezelfde indeling als de editor: die van het profiel als die er is, anders de standaard.
 */
export async function addWidgetToHome(w: Omit<WidgetDef, 'id'>): Promise<void> {
  const cur = await layoutApi.current();
  const scope: 'profile' | 'default' = cur.scope === 'default' && cur.canEditDefault ? 'default' : 'profile';
  const layout: LayoutDef = JSON.parse(JSON.stringify(cur.layout)) as LayoutDef;
  const hub = layout.hubs[0];
  if (!hub) return;
  const id = Math.random().toString(36).slice(2, 10);
  const at = ['spotlight', 'billboard'].includes(hub.widgets[0]?.view ?? '') ? 1 : 0;
  hub.widgets.splice(at, 0, { ...w, id });
  const r = await layoutApi.save(scope, layout);
  setLayout(r.layout);
}
